import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, RotateCcw } from 'lucide-react';
import {
  fetchCarrierCa,
  type CarrierCaRow,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { formatTnd, int } from '../lib/format.js';
import { cn } from '../ui/cn.js';

function KpiCard(props: {
  label: string;
  value: string;
  sub: string;
  accent: string;
}): React.ReactElement {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
      <div className="text-[11px] font-bold uppercase tracking-wider text-ink-ghost">{props.label}</div>
      <div className={cn('mt-1 text-2xl font-black tabular-nums', props.accent)}>{props.value}</div>
      <div className="mt-0.5 text-xs text-ink-soft">{props.sub}</div>
    </div>
  );
}

function CarrierRow({ row }: { row: CarrierCaRow }): React.ReactElement {
  return (
    <Tr>
      <Td className="font-bold text-ink">{row.carrierLabel}</Td>
      <Td className="text-right tabular-nums">{int(row.deliveredCount)}</Td>
      <Td className="text-right font-bold tabular-nums text-emerald-950">{formatTnd(row.deliveredCod)}</Td>
      <Td className="text-right tabular-nums text-ink-soft">{formatTnd(row.excelCod)}</Td>
      <Td className="text-right tabular-nums text-ink-soft">{formatTnd(row.shiparonCod)}</Td>
      <Td className="text-right tabular-nums">{int(row.returnedCount)}</Td>
      <Td className="text-right tabular-nums text-ink-ghost">{int(row.inTransitCount)}</Td>
      <Td className="text-right tabular-nums">
        <span title={`Tarif ${row.deliveryPriceHt} HT / retour ${row.returnPriceHt} HT (TVA ${Math.round(row.vatRate * 100)} %)`}>
          {formatTnd(row.totalFeesHt)}
        </span>
      </Td>
      <Td className="text-right tabular-nums">{formatTnd(row.totalFeesTtc)}</Td>
      <Td className="text-right font-black tabular-nums text-ink">{formatTnd(row.netPayout)}</Td>
    </Tr>
  );
}

export function CarrierCaPage(): React.ReactElement {
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const periodInvalid = from !== '' && to !== '' && from > to;

  const query = useQuery({
    queryKey: ['carrier-ca', from, to],
    queryFn: () => fetchCarrierCa({ from: from || undefined, to: to || undefined }),
    enabled: !periodInvalid,
    staleTime: 60_000,
  });

  const data = query.data;
  const totals = data?.totals;

  return (
    <div className="space-y-5">
      <PageHeader
        index="S26"
        eyebrow="COMPTABILITÉ"
        title="CA Carrier — Collecté vs Frais par Transporteur"
        purpose="Chiffre d'affaires collecté par transporteur (COD livrés : bordereaux Excel + Shiparon) face aux frais de livraison payés. Net reversé = collecté − frais HT."
      />

      <Panel className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="p-2.5 bg-indigo-100/70 text-indigo-700 rounded-xl border border-indigo-200">
            <Calendar className="h-5 w-5" />
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
            De
            <input
              type="month"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-lg border border-border bg-card px-2 py-1.5 font-mono text-sm text-ink"
            />
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
            À
            <input
              type="month"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-lg border border-border bg-card px-2 py-1.5 font-mono text-sm text-ink"
            />
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
            className="gap-1.5 text-xs font-semibold"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>Toute la période</span>
          </Button>
          {periodInvalid && (
            <span className="text-xs font-semibold text-red-700">La borne « De » doit précéder « À ».</span>
          )}
          {data?.period && (
            <span className="text-xs text-ink-ghost">
              Période : {data.period.from ?? '…'} → {data.period.to ?? '…'}
            </span>
          )}
          {data === undefined && !periodInvalid && (
            <span className="text-xs text-ink-ghost">Période : tout l'historique</span>
          )}
        </div>
      </Panel>

      {query.isPending && <Panel className="p-6 text-sm text-ink-soft">Chargement du CA par transporteur…</Panel>}
      {query.isError && (
        <Panel className="p-6 text-sm font-semibold text-red-700">
          Échec du chargement ({query.error instanceof Error ? query.error.message : 'erreur inconnue'}).
        </Panel>
      )}

      {totals && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard
              label="COD collecté (livrés)"
              value={formatTnd(totals.deliveredCod)}
              sub={`${int(totals.deliveredCount)} colis — Excel ${formatTnd(totals.excelCod)} · Shiparon ${formatTnd(totals.shiparonCod)}`}
              accent="text-emerald-950"
            />
            <KpiCard
              label="Frais de livraison"
              value={formatTnd(totals.totalFeesTtc)}
              sub={`${formatTnd(totals.totalFeesHt)} HT · ${int(totals.returnedCount)} retours (${formatTnd(totals.returnedCod)})`}
              accent="text-ink"
            />
            <KpiCard
              label="Net reversé attendu"
              value={formatTnd(totals.netPayout)}
              sub={`${int(totals.carrierCount)} transporteurs · ${int(totals.inTransitCount)} colis en cours (${formatTnd(totals.inTransitCod)})`}
              accent="text-indigo-900"
            />
          </div>

          <TableShell>
            <Table>
              <THead>
                <Tr>
                  <Th>Transporteur</Th>
                  <Th className="text-right">Livrés</Th>
                  <Th className="text-right">COD collecté</Th>
                  <Th className="text-right">dont Excel</Th>
                  <Th className="text-right">dont Shiparon</Th>
                  <Th className="text-right">Retours</Th>
                  <Th className="text-right">En cours</Th>
                  <Th className="text-right">Frais HT</Th>
                  <Th className="text-right">Frais TTC</Th>
                  <Th className="text-right">Net reversé</Th>
                </Tr>
              </THead>
              <TBody>
                {data.carriers.map((row) => (
                  <CarrierRow key={row.carrierKey} row={row} />
                ))}
                <Tr className="bg-cream/60 font-bold">
                  <Td>Total ({totals.carrierCount})</Td>
                  <Td className="text-right tabular-nums">{int(totals.deliveredCount)}</Td>
                  <Td className="text-right tabular-nums">{formatTnd(totals.deliveredCod)}</Td>
                  <Td className="text-right tabular-nums">{formatTnd(totals.excelCod)}</Td>
                  <Td className="text-right tabular-nums">{formatTnd(totals.shiparonCod)}</Td>
                  <Td className="text-right tabular-nums">{int(totals.returnedCount)}</Td>
                  <Td className="text-right tabular-nums">{int(totals.inTransitCount)}</Td>
                  <Td className="text-right tabular-nums">{formatTnd(totals.totalFeesHt)}</Td>
                  <Td className="text-right tabular-nums">{formatTnd(totals.totalFeesTtc)}</Td>
                  <Td className="text-right tabular-nums">{formatTnd(totals.netPayout)}</Td>
                </Tr>
              </TBody>
            </Table>
          </TableShell>

          <p className="text-[11px] text-ink-ghost">
            Frais calculés avec la grille en vigueur (modifiable dans « Tarifs transporteurs », page Delivered by
            YM). Retours facturés selon la grille ; en-cours exclus du collecté et du net.
          </p>
        </>
      )}
    </div>
  );
}
